// src/pages/app/ProfilePage.tsx
import React, { useEffect, useState, useRef } from "react";
import {
  AccountMe,
  fetchAccountMe,
  updateAccountProfile,
  changePasswordApi,
  deleteAccountWithPassword,
  deleteAccountWithGoogle,
  startTotpSetup,
  confirmTotpSetup,
  disableTotp
} from "../../api/account";

import { clearAuthData } from "../../api/auth";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";

declare global {
  interface Window {
    google?: any;
  }
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();

  const [account, setAccount] = useState<AccountMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // MFA state
  const [mfaSaving, setMfaSaving] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSuccess, setMfaSuccess] = useState<string | null>(null);

  const [totpSetupToken, setTotpSetupToken] = useState<string | null>(null);
  const [totpOtpauthUrl, setTotpOtpauthUrl] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [totpCodeInput, setTotpCodeInput] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const [disableMfaMode, setDisableMfaMode] = useState(false);
  const [disableMfaCode, setDisableMfaCode] = useState("");
  const [disableMfaBackupCode, setDisableMfaBackupCode] = useState("");

  const navigate = useNavigate();

  const [deletePassword, setDeletePassword] = useState("");
  const [deleteSaving, setDeleteSaving] = useState(false);

  const googleDeleteButtonRef = useRef<HTMLDivElement | null>(null);

  const resetAllNotices = () => {
    setError(null);
    setProfileSuccess(null);
    setPasswordSuccess(null);
    setMfaError(null);
    setMfaSuccess(null);
  };

  const confirmDangerousDelete = (): boolean => {
    return window.confirm(t("profile.confirmDelete"));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchAccountMe()
      .then((me) => {
        if (cancelled) return;
        setAccount(me);
        setName(me.name || "");
        setAvatarUrl(me.avatarUrl || "");
      })
      .catch((err: any) => {
        console.error(err);
        if (!cancelled) {
          setError(err?.message || t("profile.errors.loadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!account || account.authProvider !== "google") return;

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn("VITE_GOOGLE_CLIENT_ID is not set; Google delete disabled.");
      return;
    }

    if (!window.google || !window.google.accounts?.id) {
      console.warn("Google Identity Services script not available.");
      return;
    }

    if (!googleDeleteButtonRef.current) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: async (response: any) => {
        const idToken = response.credential;
        if (!idToken) return;

        const confirmed = confirmDangerousDelete();
        if (!confirmed) return;

        setDeleteSaving(true);
        resetAllNotices();

        try {
          const res = await deleteAccountWithGoogle(idToken);
          clearAuthData();
          navigate("/login", { replace: true });
        } catch (err: any) {
          console.error(err);
          setError(err?.message || t("profile.errors.deleteFailed"));
        } finally {
          setDeleteSaving(false);
        }
      }
    });

    window.google.accounts.id.renderButton(googleDeleteButtonRef.current, {
      theme: "outline",
      size: "large",
      text: "continue_with"
    });
  }, [account, navigate, t]);

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account) return;
    if (!deletePassword) return;

    const confirmed = confirmDangerousDelete();
    if (!confirmed) return;

    setDeleteSaving(true);
    resetAllNotices();

    try {
      const res = await deleteAccountWithPassword(deletePassword);
      clearAuthData();
      navigate("/login", { replace: true });
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("profile.errors.deleteFailed"));
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account) return;

    setProfileSaving(true);
    resetAllNotices();

    try {
      const updated = await updateAccountProfile({
        name: name.trim() || undefined,
        avatarUrl: avatarUrl.trim() || undefined
      });
      setAccount(updated);
      setProfileSuccess(t("profile.profile.success"));
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("profile.errors.updateProfileFailed"));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account || !account.hasPassword) return;

    if (newPassword !== confirmPassword) {
      setError(t("profile.password.errors.mismatch"));
      return;
    }

    setPasswordSaving(true);
    resetAllNotices();

    try {
      const res = await changePasswordApi(currentPassword, newPassword);
      setPasswordSuccess(res.message || t("profile.password.success"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("profile.errors.changePasswordFailed"));
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleStartTotpSetup = async () => {
    if (!account) return;

    setMfaError(null);
    setMfaSuccess(null);
    setError(null);
    setProfileSuccess(null);
    setPasswordSuccess(null);

    setMfaSaving(true);
    try {
      const res = await startTotpSetup();
      setTotpSetupToken(res.setupToken);
      setTotpOtpauthUrl(res.otpauthUrl);
      setTotpSecret(res.secret);
      setTotpCodeInput("");
      setBackupCodes([]);
    } catch (err: any) {
      console.error(err);
      setMfaError(err?.message || t("profile.mfa.errors.startFailed"));
    } finally {
      setMfaSaving(false);
    }
  };

  const handleConfirmTotpSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totpSetupToken) return;

    setMfaError(null);
    setMfaSuccess(null);
    setError(null);
    setProfileSuccess(null);
    setPasswordSuccess(null);

    setMfaSaving(true);
    try {
      const res = await confirmTotpSetup(totpSetupToken, totpCodeInput.trim());
      setBackupCodes(res.backupCodes || []);
      setTotpSetupToken(null);
      setTotpOtpauthUrl(null);
      setTotpSecret(null);
      setTotpCodeInput("");
      setDisableMfaMode(false);

      setAccount((prev) => (prev ? { ...prev, mfaEnabled: res.mfaEnabled } : prev));
      setMfaSuccess(t("profile.mfa.success.enabledSaveBackupCodes"));
    } catch (err: any) {
      console.error(err);
      setMfaError(err?.message || t("profile.mfa.errors.confirmFailed"));
    } finally {
      setMfaSaving(false);
    }
  };

  const handleDisableTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account) return;

    if (!disableMfaCode && !disableMfaBackupCode) {
      setMfaError(t("profile.mfa.errors.disableRequiresCode"));
      return;
    }

    setMfaSaving(true);
    setMfaError(null);
    setMfaSuccess(null);
    setError(null);
    setProfileSuccess(null);
    setPasswordSuccess(null);

    try {
      const res = await disableTotp({
        code: disableMfaCode.trim() || undefined,
        backupCode: disableMfaBackupCode.trim() || undefined
      });

      setAccount((prev) => (prev ? { ...prev, mfaEnabled: res.mfaEnabled } : prev));
      setDisableMfaMode(false);
      setDisableMfaCode("");
      setDisableMfaBackupCode("");
      setBackupCodes([]);
      setTotpSetupToken(null);
      setTotpOtpauthUrl(null);
      setTotpSecret(null);
      setMfaSuccess(res.message || t("profile.mfa.success.disabled"));
    } catch (err: any) {
      console.error(err);
      setMfaError(err?.message || t("profile.mfa.errors.disableFailed"));
    } finally {
      setMfaSaving(false);
    }
  };

  const handleCopyBackupCodes = async () => {
    if (!backupCodes.length) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setMfaSuccess(t("profile.mfa.success.backupCodesCopied"));
    } catch {
      setMfaError(t("profile.mfa.errors.backupCodesCopyFailed"));
    }
  };

  const initials = (() => {
    if (name.trim()) {
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    }
    if (account?.email) {
      return account.email[0].toUpperCase();
    }
    return "?";
  })();

  const loginMethodLabel = (() => {
    if (!account) return t("profile.security.loginMethod.unknown");
    if (account.authProvider === "google") return t("profile.security.loginMethod.google");
    if (account.hasPassword) return t("profile.security.loginMethod.emailPassword");
    return t("profile.security.loginMethod.unknown");
  })();

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{t("profile.pageTitle")}</h1>
          <p className="muted">{t("profile.pageSubtitle")}</p>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {profileSuccess && <div className="form-success">{profileSuccess}</div>}
      {passwordSuccess && <div className="form-success">{passwordSuccess}</div>}
      {mfaSuccess && <div className="form-success">{mfaSuccess}</div>}

      {loading && (
        <div className="detail-main" style={{ marginTop: "1rem" }}>
          <p>{t("profile.loading")}</p>
        </div>
      )}

      {!loading && account && (
        <>
          {/* Profile card */}
          <section className="detail-main" style={{ marginTop: "1rem" }}>
            <div className="profile-header-row">
              <div className="profile-avatar">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={t("profile.profile.avatarAlt")} />
                ) : (
                  <span className="profile-avatar-initials">{initials}</span>
                )}
              </div>

              <div>
                <h2 style={{ margin: 0 }}>{t("profile.profile.title")}</h2>
                <p className="muted" style={{ marginTop: "0.25rem" }}>
                  {t("profile.profile.subtitle")}
                </p>
              </div>
            </div>

            <form className="form" onSubmit={handleSaveProfile}>
              <div className="form-field">
                <label>{t("profile.profile.displayName.label")}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("profile.profile.displayName.placeholder")}
                />
              </div>

              <div className="form-field">
                <label>{t("profile.profile.avatarUrl.label")}</label>
                <input
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder={t("profile.profile.avatarUrl.placeholder")}
                />
                <span>{t("profile.profile.avatarUrl.help")}</span>
              </div>

              <div>
                <button className="btn-primary" type="submit" disabled={profileSaving}>
                  {profileSaving ? t("profile.profile.actions.saving") : t("profile.profile.actions.save")}
                </button>
              </div>
            </form>
          </section>

          {/* Login & security */}
          <section className="detail-main" style={{ marginTop: "1.5rem" }}>
            <h2>{t("profile.security.title")}</h2>

            <div style={{ marginTop: "0.7rem" }}>
              <p className="muted">
                <strong>{t("profile.security.emailLabel")}:</strong> {account.email}
              </p>
              <p className="muted">
                <strong>{t("profile.security.loginMethodLabel")}:</strong> {loginMethodLabel}
              </p>
            </div>

            {account.hasPassword ? (
              <form className="form" style={{ marginTop: "1rem" }} onSubmit={handleChangePassword}>
                <div className="form-field">
                  <label>{t("profile.password.current.label")}</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <label>{t("profile.password.new.label")}</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <span>{t("profile.password.new.help")}</span>
                </div>

                <div className="form-field">
                  <label>{t("profile.password.confirm.label")}</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                <div>
                  <button className="btn-secondary" type="submit" disabled={passwordSaving}>
                    {passwordSaving ? t("profile.password.actions.updating") : t("profile.password.actions.change")}
                  </button>
                </div>
              </form>
            ) : (
              <p className="muted" style={{ marginTop: "1rem" }}>
                {t("profile.security.googleOnlyNoteStrongPrefix")}{" "}
                <strong>{t("profile.security.googleSignInStrong")}</strong>.{" "}
                {t("profile.security.googleOnlyNoteSuffix")}
              </p>
            )}
          </section>

          {/* Two-factor authentication */}
          <section className="detail-main" style={{ marginTop: "1.5rem" }}>
            <h2>{t("profile.mfa.title")}</h2>
            <p className="muted" style={{ marginTop: "0.5rem" }}>
              {t("profile.mfa.subtitle")}
            </p>

            {mfaError && (
              <div className="form-error" style={{ marginTop: "0.75rem" }}>
                {mfaError}
              </div>
            )}

            <div style={{ marginTop: "1rem" }}>
              <p className="muted">
                <strong>{t("profile.mfa.statusLabel")}:</strong>{" "}
                {account.mfaEnabled ? (
                  <span className="badge badge-success">{t("profile.mfa.status.enabled")}</span>
                ) : (
                  <span className="badge badge-muted">{t("profile.mfa.status.disabled")}</span>
                )}
              </p>
            </div>

            {/* Setup flow */}
            {!account.mfaEnabled && !totpSetupToken && (
              <div style={{ marginTop: "1rem" }}>
                <p className="muted">{t("profile.mfa.recommendation")}</p>
                <button
                  className="btn-secondary"
                  type="button"
                  disabled={mfaSaving}
                  onClick={handleStartTotpSetup}
                >
                  {mfaSaving ? t("profile.mfa.actions.preparing") : t("profile.mfa.actions.enable")}
                </button>
              </div>
            )}

            {/* Show QR + code confirmation */}
            {!account.mfaEnabled && totpSetupToken && (
              <div
                className="mfa-setup-grid"
                style={{ marginTop: "1.25rem", display: "grid", gap: "1.25rem" }}
              >
                <div>
                  <h3 style={{ marginTop: 0 }}>{t("profile.mfa.setup.step1Title")}</h3>
                  <p className="muted">{t("profile.mfa.setup.step1Body")}</p>

                  <div
                    style={{
                      marginTop: "0.75rem",
                      padding: "0.75rem",
                      borderRadius: "0.75rem",
                      border: "1px solid var(--border-subtle)",
                      display: "inline-block",
                      background: "var(--bg-subtle)"
                    }}
                  >
                    {totpOtpauthUrl && (
                      <QRCodeSVG value={totpOtpauthUrl} width={160} height={160} />
                    )}
                  </div>

                  {totpSecret && (
                    <div style={{ marginTop: "0.75rem" }}>
                      <p className="muted" style={{ marginBottom: "0.25rem" }}>
                        {t("profile.mfa.setup.manualSecretLabel")}
                      </p>
                      <code
                        style={{
                          display: "inline-block",
                          padding: "0.25rem 0.5rem",
                          borderRadius: "0.5rem",
                          background: "var(--bg-subtle)"
                        }}
                      >
                        {totpSecret}
                      </code>
                    </div>
                  )}
                </div>

                <div>
                  <h3 style={{ marginTop: 0 }}>{t("profile.mfa.setup.step2Title")}</h3>
                  <p className="muted">{t("profile.mfa.setup.step2Body")}</p>

                  <form className="form" style={{ marginTop: "0.75rem" }} onSubmit={handleConfirmTotpSetup}>
                    <div className="form-field">
                      <label>{t("profile.mfa.setup.codeLabel")}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={6}
                        value={totpCodeInput}
                        onChange={(e) => setTotpCodeInput(e.target.value)}
                        required
                      />
                    </div>

                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button className="btn-primary" type="submit" disabled={mfaSaving}>
                        {mfaSaving ? t("profile.mfa.actions.verifying") : t("profile.mfa.actions.confirmEnable")}
                      </button>
                      <button
                        type="button"
                        className="btn-ghost"
                        disabled={mfaSaving}
                        onClick={() => {
                          setTotpSetupToken(null);
                          setTotpOtpauthUrl(null);
                          setTotpSecret(null);
                          setTotpCodeInput("");
                          setBackupCodes([]);
                        }}
                      >
                        {t("profile.common.cancel")}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Enabled state */}
            {account.mfaEnabled && (
              <div style={{ marginTop: "1.25rem" }}>
                <p className="muted">{t("profile.mfa.enabledNote")}</p>

                {backupCodes.length > 0 && (
                  <div
                    className="mfa-backup-codes"
                    style={{
                      marginTop: "1rem",
                      padding: "1rem",
                      borderRadius: "0.75rem",
                      border: "1px dashed var(--border-subtle)",
                      background: "var(--bg-subtle)"
                    }}
                  >
                    <h3 style={{ marginTop: 0 }}>{t("profile.mfa.backupCodes.title")}</h3>
                    <p className="muted">{t("profile.mfa.backupCodes.body")}</p>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                        gap: "0.4rem",
                        marginTop: "0.75rem"
                      }}
                    >
                      {backupCodes.map((code) => (
                        <code
                          key={code}
                          style={{
                            display: "inline-block",
                            padding: "0.35rem 0.5rem",
                            borderRadius: "0.5rem",
                            textAlign: "center",
                            background: "var(--bg-elevated)"
                          }}
                        >
                          {code}
                        </code>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ marginTop: "0.75rem" }}
                      onClick={handleCopyBackupCodes}
                    >
                      {t("profile.mfa.backupCodes.copyAll")}
                    </button>
                  </div>
                )}

                <div
                  className="mfa-disable"
                  style={{
                    marginTop: "1.5rem",
                    paddingTop: "1rem",
                    borderTop: "1px solid var(--border-subtle)"
                  }}
                >
                  {!disableMfaMode ? (
                    <>
                      <p className="muted">{t("profile.mfa.disable.intro")}</p>
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={() => {
                          setDisableMfaMode(true);
                          setDisableMfaCode("");
                          setDisableMfaBackupCode("");
                          setMfaError(null);
                          setMfaSuccess(null);
                        }}
                      >
                        {t("profile.mfa.disable.open")}
                      </button>
                    </>
                  ) : (
                    <form className="form" style={{ marginTop: "0.75rem" }} onSubmit={handleDisableTotp}>
                      <p className="muted">{t("profile.mfa.disable.confirmBody")}</p>

                      <div className="form-field">
                        <label>{t("profile.mfa.disable.codeLabel")}</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="\d*"
                          maxLength={6}
                          value={disableMfaCode}
                          onChange={(e) => setDisableMfaCode(e.target.value)}
                        />
                      </div>

                      <div className="form-field">
                        <label>{t("profile.mfa.disable.backupCodeLabel")}</label>
                        <input
                          type="text"
                          value={disableMfaBackupCode}
                          onChange={(e) => setDisableMfaBackupCode(e.target.value)}
                        />
                      </div>

                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button className="btn-danger" type="submit" disabled={mfaSaving}>
                          {mfaSaving ? t("profile.mfa.actions.disabling") : t("profile.mfa.disable.confirm")}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost"
                          disabled={mfaSaving}
                          onClick={() => {
                            setDisableMfaMode(false);
                            setDisableMfaCode("");
                            setDisableMfaBackupCode("");
                          }}
                        >
                          {t("profile.common.cancel")}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Danger zone: account deletion */}
          <section className="detail-main" style={{ marginTop: "1.5rem" }}>
            <h2>{t("profile.danger.title")}</h2>
            <p className="muted" style={{ marginTop: "0.5rem" }}>
              {t("profile.danger.subtitle")}
            </p>

            {account.hasPassword ? (
              <form className="form" style={{ marginTop: "1rem" }} onSubmit={handleDeleteAccount}>
                <div className="form-field">
                  <label>{t("profile.danger.passwordConfirm.label")}</label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                  />
                  <span>{t("profile.danger.passwordConfirm.help")}</span>
                </div>

                <div>
                  <button
                    className="btn-danger"
                    type="submit"
                    disabled={deleteSaving || !deletePassword}
                  >
                    {deleteSaving ? t("profile.danger.actions.deleting") : t("profile.danger.actions.delete")}
                  </button>
                </div>
              </form>
            ) : account.authProvider === "google" ? (
              <div style={{ marginTop: "1rem" }}>
                <p className="muted">{t("profile.danger.googleNote")}</p>
                <div style={{ marginTop: "0.8rem" }}>
                  <div ref={googleDeleteButtonRef} />
                </div>
                {deleteSaving && (
                  <p className="muted" style={{ marginTop: "0.5rem" }}>
                    {t("profile.danger.actions.deletingInline")}
                  </p>
                )}
              </div>
            ) : (
              <div className="alert-warning" style={{ marginTop: "1rem" }}>
                {t("profile.danger.unsupported")}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ProfilePage;
